import React, { useMemo, useState } from "react";
import { Inertia } from "@inertiajs/inertia";
import { useTitle } from "@/Utilities/Hooks";
import { ActivityEmissions } from "@/Components/";
import { LabeledInput } from "@/Components/Form";
import { convertToHumanReadable, formatDate } from "@/Utilities/Utilities";
import { DynamicInputType } from "@/Components/Questions/InputTypes/Report";
import { reportQuestionsAtom } from "@/Stores";
import { useAtom } from "jotai";
import { toast } from "react-toastify";
import ExternalLayout from "@/Layouts/ExternalLayout";
import { Tag } from "@/Components/Tag";
import { Button } from "@/Components";
import { DashboardBarChart, getChartColor } from "@/Utilities/V2Graphs";

export default function ExternalReport({
  errors,
  calculatorResource,
  recipientResource,
  organizationResource,
  emission_graph = [],
}) {
  useTitle("Report emissions");

  const [answers] = useAtom(reportQuestionsAtom);

  let recipient = recipientResource?.data || {};
  const calculator = calculatorResource?.data || {};
  const organization = organizationResource?.data || {};
  const [reportedData, setReportedData] = useState(
    recipient?.reported_data || false,
  );

  const [recipientName, setRecipientName] = useState(recipient?.name || "");
  const [recipientEmail, setRecipientEmail] = useState(recipient?.email || "");
  const [termsAccepted, setTermsAccepted] = useState(
    !!recipient?.reported_data,
  );
  const [toggleReviewAnswers, setToggleReviewAnswers] = useState(false);

  function handleSubmit(e) {
    e.preventDefault();

    const id = toast.loading("Please wait...");

    const payload = {
      name: recipientName,
      questions: Object.keys(answers).map((question_id) => ({
        question_id: question_id,
        values: answers[question_id].answers,
      })),
    };

    let options = {};

    if (!recipient?.email) {
      payload.email = recipientEmail;
    } else {
      options = {
        ...options,
        preserveState: false,
      };
    }

    Inertia.post(window.location.href, payload, {
      ...options,
      preserveScroll: false,
      onSuccess: (page) => {
        const newProps = page.props.custom_response_data;

        toast.update(id, {
          render: "You have successfully reported!",
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          autoClose: 5000,
        });
        setReportedData(newProps.reported_data);
        setToggleReviewAnswers(false);

        if (!recipient?.email) {
          // Guest user was reporting, update the recipient object and URL
          recipient = newProps?.recipient?.data || {};
          // window.history.replaceState(null, "", newProps.unique_url);
          Inertia.visit(newProps.unique_url);
        }
      },
      onError: (errors) => {
        toast.update(id, {
          render: "There was an error while reporting. Please check your input",
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
        });
      },
    });
  }

  const reportedEmission = Object.values(reportedData || {}).reduce(
    (accumulator, current) => {
      return accumulator + (current?.parsed?.emission?.total || 0);
    },
    0,
  );

  return (
    <div className="relative">
      <div className="absolute bottom-0 right-0 top-0 w-7/12 bg-gray-light max-md:w-full" />
      <img
        loading="lazy"
        src="/images/decorative-polygon-lightblue.svg"
        alt=""
        role="presentation"
        className="pointer-events-none absolute bottom-[40%] right-0 w-[145px] max-w-full max-md:hidden"
      />

      <div className="mx-auto flex h-full min-h-[calc(100lvh_-_89px)] w-full max-w-[1440px] justify-between max-md:flex-col max-md:gap-4">
        <div className="z-10 flex w-5/12 flex-col gap-4 bg-white px-16 py-32 max-lg:px-8 max-md:w-full max-md:p-4 2xl:pl-0">
          <img srcSet={calculator.logo} className="h-auto max-w-32" />
          <h1 className="header-1 mb-0">{calculator.name}</h1>
          <p className="font-bold">
            <a
              href={`mailto:${calculator.creator.email}`}
              className="underline hover:no-underline"
            >
              {calculator.creator.fullname}
            </a>{" "}
            at {organization.name} would like your help in answering
            sustainability-related questions. Please report before{" "}
            {formatDate(calculator.expiry_date)}
          </p>
          <p>{calculator.description}</p>
        </div>

        <div className="relative flex w-7/12 flex-col gap-4 p-16 max-lg:p-8 max-md:w-full max-md:p-4 2xl:pr-0">
          <img
            loading="lazy"
            src="/images/decorative-polygon-darkblue.svg"
            alt=""
            role="presentation"
            className="pointer-events-none absolute left-0 top-[10%] w-[145px] max-w-full opacity-20 max-md:hidden"
          />

          <img
            loading="lazy"
            src="/images/decorative-polygon-yellow.svg"
            alt=""
            role="presentation"
            className="pointer-events-none absolute bottom-[10%] left-0 w-[145px] max-w-full max-md:hidden"
          />

          {reportedData && (
            <>
              <div className="z-10 bg-white p-4 shadow-default lg:p-8">
                <h3 className="mb-4 text-2xl font-medium">
                  Thank you for reporting, {recipient.email}
                </h3>
                <p className="mt-4">
                  You can come back and change your report until{" "}
                  {formatDate(calculator.expiry_date)}
                </p>
                <div className="mt-8 flex flex-col items-center justify-center bg-yellow p-4 lg:p-8">
                  <p>You have reported</p>

                  <p className="header-1 mb-0 mt-2 font-rozhaone">{`${convertToHumanReadable(reportedEmission)} kg CO₂e`}</p>
                </div>

                {emission_graph && emission_graph.length > 0 && (
                  <BarChart data={emission_graph} />
                )}
              </div>
              {!toggleReviewAnswers && (
                <Button
                  className="mt-4 w-full justify-center"
                  showArrowRight={false}
                  onClick={() => setToggleReviewAnswers(true)}
                >
                  Review answers
                </Button>
              )}
            </>
          )}

          {(!reportedData || toggleReviewAnswers) &&
            calculator.questions.map((question) => (
              <div
                key={question.id}
                className="z-10 bg-white p-4 shadow-default lg:p-8"
              >
                {question.tags &&
                  question.tags.map((tag) => (
                    <Tag key={tag.id} margin="mr-2">
                      {tag.name}
                    </Tag>
                  ))}
                <h3 className="my-4 text-xl font-medium">{question.title}</h3>
                <DynamicInputType
                  question={question}
                  values={reportedData ? reportedData[question.id].raw : null}
                />
              </div>
            ))}

          {!reportedData && (
            <div className="z-10 flex flex-col gap-4 bg-white p-4 shadow-default lg:p-8">
              <h3 className="text-xl font-medium">About you</h3>
              <p>We just need a little info about you before you leave.</p>
              <div className="w-full">
                <LabeledInput
                  id="name"
                  name="name"
                  value={recipientName}
                  title="Name or organization*"
                  placeholder="Enter your name or organization"
                  onChange={(e) => setRecipientName(e.target.value)}
                  error={errors.name}
                />
              </div>

              {!recipient?.email && (
                <LabeledInput
                  id="email"
                  name="email"
                  value={recipientEmail}
                  title="E-mail*"
                  placeholder="Enter your e-mail address"
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  error={errors.email}
                />
              )}

              <div>
                <input
                  type="checkbox"
                  id="terms"
                  className="mr-2"
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  checked={termsAccepted}
                />
                <label htmlFor="terms">
                  I agree to Gidås{" "}
                  <a
                    target="_blank"
                    className="underline hover:no-underline"
                    href="/terms-of-service"
                  >
                    Terms of Service
                  </a>
                  *
                </label>
              </div>
            </div>
          )}
          {(!reportedData || toggleReviewAnswers) && (
            <Button
              disabled={!termsAccepted}
              onClick={handleSubmit}
              className="mt-4 w-full justify-center"
            >
              Submit answers
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

ExternalReport.layout = (page) => <ExternalLayout>{page}</ExternalLayout>;

const BarChart = ({ data }) => {
  const dataSets = useMemo(() => {
    return data.map((item, i) => ({
      label: item.label,
      barPercentage: 0.5,
      borderColor: getChartColor(i + 1),
      backgroundColor: getChartColor(i + 1),
      data: [{ y: item.value }],
    }));
  }, [data]);

  const labels = useMemo(() => data.map((item) => item.label), [data]);

  return (
    <div className="mt-8 w-full">
      <DashboardBarChart dataSets={dataSets} labels={labels} />
    </div>
  );
};
